//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  BulkUploadProductsParseResult,
  CreateSamplingCampaignRequestResponse,
  DeleteSamplingCampaignRequestPayload,
  EditSamplingCampaignRequestPayload,
  EditSamplingCampaignRequestResponse,
  GetClientSamplingCampaignRequestByIdRequest,
  GetClientSamplingCampaignRequestsRequest,
  GetSamplingCampaignRequestQuoteRequest,
  GetSamplingCampaignRequestQuoteResponse,
  GetSamplingCampaignRequestResponse,
  GetSamplingCampaignRequestsResponse,
  ParseBulkUploadProductsResponse,
  ProcessBulkUploadProductsRequestPayload,
  RequestCampaignRequestQuoteRequest,
  SamplingCampaignRequest,
  SamplingCampaignRequestQuote,
  SamplingCampaignRequestsResponse,
  StartInspirationVideoUploadResponse,
  TransformBulkUploadProductsQueryParams,
  TransformBulkUploadProductsRequestPayload,
  TransformBulkUploadProductsResponse,
  UploadCampaignRequestFilePayload,
  UploadCampaignRequestFileResponse,
  UploadStatusResponse,
  UploadedAssetUrl,
} from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import {
  deserializeCampaignRequest,
  deserializeCampaignRequestReduced,
  deserializeProduct,
  serializeCampaignRequest,
} from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiUtils';
import { BVSamplingProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiModel';
import { SamplingCampaignRequestId } from '@models/SamplingCampaignRequestId';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const CAMPAIGN_REQUESTS_DEFAULT_ENDPOINT = '/api/v1/clients/campaign-requests';

export const BACKEND_ERROR_MESSAGES = {
  MISSING_BV_CLIENT_ID: 'Missing BV client ID for SoCo client ID',
};

const campaignRequestsFetch = (
  endpoint: string,
  init?: RequestInit,
  useFetchHeader?: boolean,
): Promise<ISerializedResponse> =>
  serializedGatewayFetch(
    'sampling_gateway',
    `${CAMPAIGN_REQUESTS_DEFAULT_ENDPOINT}${endpoint}`,
    init,
    useFetchHeader,
  );

const samplingProductsFetch = (
  endpoint: string,
  init?: RequestInit,
  useFetchHeader?: boolean,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'sampling_gateway',
    `${CAMPAIGN_REQUESTS_DEFAULT_ENDPOINT}/products${endpoint}`,
    init,
    useFetchHeader,
  );
};

/**
 * Gets all campaigns request for a specific client
 *
 * GET /api/v1/campaign-requests
 */
export const getClientCampaignRequests = async (
  req: GetClientSamplingCampaignRequestsRequest,
  init?: RequestInit,
): Promise<SamplingCampaignRequestsResponse> => {
  const params = makeParams(req);

  const res = await campaignRequestsFetch(`?${params}`, {
    ...init,
    method: 'GET',
  });

  const campaignRequestsResponse =
    await res.json<GetSamplingCampaignRequestsResponse>((res) => ({
      data: {
        ...res.data,
        campaignRequests: res.data.campaignRequests.map((campaign) =>
          deserializeCampaignRequestReduced(campaign),
        ),
      },
    }));

  return campaignRequestsResponse.data;
};

/**
 * Gets a campaign request for a specific client
 *
 * GET /api/v1/campaign-requests/:campaignId
 */

export const getClientCampaignRequest = async (
  req: GetClientSamplingCampaignRequestByIdRequest,
  init?: RequestInit,
): Promise<SamplingCampaignRequest> => {
  const res = await campaignRequestsFetch(`/${req.campaignId}`, {
    ...init,
    method: 'GET',
  });

  const campaignRequestsResponse =
    await res.json<GetSamplingCampaignRequestResponse>((res) => ({
      data: deserializeCampaignRequest(res.data),
    }));

  // Clean up deprecated sendToMeAndOthers value
  if (
    campaignRequestsResponse.data.fulfillmentOptions?.sendTo ===
    'sendToMeAndOthers'
  ) {
    campaignRequestsResponse.data.fulfillmentOptions.sendTo = 'sendToOther';
  }

  // Clean up multiple emails
  if (
    campaignRequestsResponse.data.fulfillmentOptions?.emails &&
    campaignRequestsResponse.data.fulfillmentOptions.emails.length > 1 &&
    campaignRequestsResponse.data.fulfillmentOptions.emails[0]
  ) {
    campaignRequestsResponse.data.fulfillmentOptions.emails = [
      campaignRequestsResponse.data.fulfillmentOptions.emails[0],
    ];
  }

  return campaignRequestsResponse.data;
};

/**
 * Creates a new campaign request for a specific client
 *
 * POST /api/v1/campaign-requests
 */
export const createClientCampaignRequest = async (
  req: SamplingCampaignRequest,
  init?: RequestInit,
): Promise<SamplingCampaignRequest> => {
  const serializedCampaign = serializeCampaignRequest(req);

  const res = await campaignRequestsFetch('', {
    ...init,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(serializedCampaign),
  });

  const createSamplingCampaignRequestResponse =
    await res.json<CreateSamplingCampaignRequestResponse>((res) => ({
      data: deserializeCampaignRequest(res.data),
    }));

  return createSamplingCampaignRequestResponse.data;
};

/**
 * Edit a campaign request for a specific client
 *
 * PATCH /api/v1/clients/campaign-requests/:id/
 */
export const editClientCampaignRequest = async (
  req: EditSamplingCampaignRequestPayload,
  init?: RequestInit,
): Promise<SamplingCampaignRequest> => {
  const serializedCampaign = serializeCampaignRequest(req.campaign);

  const res = await campaignRequestsFetch(`/${req.id}`, {
    ...init,
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(serializedCampaign),
  });

  const editSamplingCampaignRequestResponse =
    await res.json<EditSamplingCampaignRequestResponse>((res) => ({
      data: deserializeCampaignRequest(res.data),
    }));

  return editSamplingCampaignRequestResponse.data;
};

/**
 * Delete a campaign request for a specific client
 *
 * DELETE /api/v1/clients/campaign-requests/:id/
 */
export const deleteClientCampaignRequest = async (
  req: DeleteSamplingCampaignRequestPayload,
  init?: RequestInit,
): Promise<void> => {
  await campaignRequestsFetch(`/${req.id}`, {
    ...init,
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

/**
 * Retrieve the current quote for a campaign request (read-only).
 *
 * GET /api/v1/clients/campaign-requests/:campaignRequestId/quote
 */
export const getCampaignRequestQuote = async (
  req: GetSamplingCampaignRequestQuoteRequest,
  init?: RequestInit,
): Promise<SamplingCampaignRequestQuote> => {
  const res = await campaignRequestsFetch(`/${req.campaignRequestId}/quote`, {
    ...init,
    method: 'GET',
  });

  const response = await res.json<GetSamplingCampaignRequestQuoteResponse>();
  return response.data;
};

/**
 * Request (or re-request) a quote for a campaign request (side-effecting).
 * Triggers asynchronous quote generation; this does not return the quote.
 *
 * POST /api/v1/clients/campaign-requests/:campaignRequestId/quote
 */
export const requestQuote = async (
  req: RequestCampaignRequestQuoteRequest,
  init?: RequestInit,
): Promise<void> => {
  await campaignRequestsFetch(`/${req.campaignRequestId}/quote`, {
    ...init,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(req.payload),
  });
};

/**
 * Transform a CSV/XLSX file into product data
 *
 * POST /api/v1/clients/campaign-requests/products/transform
 */
export const transformBulkUploadProducts = async (
  req: TransformBulkUploadProductsRequestPayload,
  queryParams?: TransformBulkUploadProductsQueryParams,
  init?: RequestInit,
): Promise<BVSamplingProduct[]> => {
  const formData = new FormData();
  formData.append('mediaItem', req.file, req.file.name);

  const params = makeParams(queryParams);
  const res = await samplingProductsFetch(
    `/transform${params ? `?${params}` : ''}`,
    {
      ...init,
      method: 'POST',
      body: formData,
    },
    true,
  );

  const response = await res.json<TransformBulkUploadProductsResponse>();
  return response.data.map(deserializeProduct);
};

/**
 * Download the CSV template used to bulk upload products
 *
 * GET /api/v1/clients/campaign-requests/products/template
 */
export const getBulkUploadProductsTemplate = async (
  init?: RequestInit,
): Promise<Blob> => {
  const res = await samplingProductsFetch('/template', {
    ...init,
    method: 'GET',
  });

  return res.blob();
};

/**
 * Read the headers of a CSV/XLSX/XLSM file of products, with a suggested mapping of its columns and sample rows.
 * Nothing is stored: the file has to be sent again to process it.
 *
 * POST /api/v1/clients/campaign-requests/products/upload/parse
 */
export const parseBulkUploadProductsFile = async (
  req: TransformBulkUploadProductsRequestPayload,
  init?: RequestInit,
): Promise<BulkUploadProductsParseResult> => {
  const formData = new FormData();
  formData.append('file', req.file, req.file.name);

  const res = await samplingProductsFetch(
    '/upload/parse',
    {
      ...init,
      method: 'POST',
      body: formData,
    },
    true,
  );

  const response = await res.json<ParseBulkUploadProductsResponse>();
  return response.data;
};

/**
 * Transform a CSV/XLSX/XLSM file of products into products using the given column mapping.
 * The file is re-sent because nothing is stored server-side after parsing.
 *
 * POST /api/v1/clients/campaign-requests/products/upload/process
 */
export const processBulkUploadProducts = async (
  req: ProcessBulkUploadProductsRequestPayload,
  init?: RequestInit,
): Promise<BVSamplingProduct[]> => {
  const formData = new FormData();
  // mapping must be appended before file – the backend streams and reads fields in order
  formData.append('mapping', JSON.stringify(req.mapping));
  formData.append('file', req.file, req.file.name);

  const res = await samplingProductsFetch(
    '/upload/process',
    { ...init, method: 'POST', body: formData },
    true,
  );

  const response = await res.json<TransformBulkUploadProductsResponse>();
  return response.data.map(deserializeProduct);
};

const postCampaignRequestFile = (
  endpoint: string,
  file: File,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  const formData = new FormData();
  formData.append('file', file, file.name);

  return campaignRequestsFetch(
    endpoint,
    {
      ...init,
      body: formData,
      method: 'POST',
    },
    true,
  );
};

/**
 * Uploads an inspiration image for a campaign request.
 *
 * POST /api/v1/clients/campaign-requests/:campaignRequestId/inspiration-images/upload
 */
export const uploadInspirationImage = async (
  { campaignRequestId, file }: UploadCampaignRequestFilePayload,
  init?: RequestInit,
): Promise<string> => {
  const res = await postCampaignRequestFile(
    `/${campaignRequestId}/inspiration-images/upload`,
    file,
    init,
  );
  const response =
    await res.json<UploadCampaignRequestFileResponse<UploadedAssetUrl>>();
  return response.data.url;
};

/**
 * Uploads a brand brief document for a campaign request. This is a
 * synchronous upload: the gateway stores the file in S3 and returns its
 * final CloudFront URL directly, with no status polling involved.
 *
 * POST /api/v1/clients/campaign-requests/:campaignRequestId/brand-brief/upload
 */
export const uploadCampaignRequestBrandBrief = async (
  { campaignRequestId, file }: UploadCampaignRequestFilePayload,
  init?: RequestInit,
): Promise<string> => {
  const res = await postCampaignRequestFile(
    `/${campaignRequestId}/brand-brief/upload`,
    file,
    init,
  );
  const response =
    await res.json<UploadCampaignRequestFileResponse<UploadedAssetUrl>>();
  return response.data.url;
};

/**
 * Starts an async content brief inspiration video upload. The gateway
 * streams the file to the video-processing service and returns immediately
 * with a VPS-owned videoId once accepted; transcoding continues in the
 * background.
 *
 * POST /api/v1/clients/campaign-requests/:campaignRequestId/content-brief/video/upload
 */
export const startInspirationVideoUpload = async (
  { campaignRequestId, file }: UploadCampaignRequestFilePayload,
  init?: RequestInit,
): Promise<StartInspirationVideoUploadResponse> => {
  const res = await postCampaignRequestFile(
    `/${campaignRequestId}/content-brief/video/upload`,
    file,
    {
      ...init,
      headers: {
        ...init?.headers,
        'Upload-Length': `${file.size}`,
      },
    },
  );
  const response =
    await res.json<
      UploadCampaignRequestFileResponse<StartInspirationVideoUploadResponse>
    >();
  return response.data;
};

/**
 * GET /api/v1/clients/campaign-requests/:campaignRequestId/content-brief/video/:videoId/status
 */
export const getInspirationVideoUploadStatus = async (
  campaignRequestId: SamplingCampaignRequestId,
  videoId: string,
  signal?: AbortSignal,
): Promise<UploadStatusResponse> => {
  const res = await campaignRequestsFetch(
    `/${campaignRequestId}/content-brief/video/${encodeURIComponent(videoId)}/status`,
    { method: 'GET', signal },
  );
  const response =
    await res.json<UploadCampaignRequestFileResponse<UploadStatusResponse>>();
  return response.data;
};
